import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCw, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function ReRaiseDialog({ open, onOpenChange, sourcePo, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);

  const fetchPreview = useCallback(async () => {
    if (!sourcePo?.id) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(API + '/purchase-orders/' + sourcePo.id + '/pending-items', {
        headers: { Authorization: 'Bearer ' + token },
      });
      setPreview(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load pending items');
    } finally {
      setLoading(false);
    }
  }, [sourcePo]);

  useEffect(() => {
    if (open) fetchPreview();
    else {
      setPreview(null);
      setError(null);
    }
  }, [open, fetchPreview]);

  const handleProceed = async () => {
    if (!sourcePo?.id) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        API + '/purchase-orders/' + sourcePo.id + '/re-raise',
        {},
        { headers: { Authorization: 'Bearer ' + token } }
      );
      toast.success(
        'New PO ' + res.data.new_po_number + ' created with ' + res.data.items_count + ' pending item' +
          (res.data.items_count === 1 ? '' : 's') + '. ' + sourcePo.po_number + ' short-closed.'
      );
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to re-raise PO');
    } finally {
      setSubmitting(false);
    }
  };

  const items = preview?.items || [];
  const summary = preview?.summary || {};
  const hasPending = items.length > 0;

  return React.createElement(Dialog, { open, onOpenChange },
    React.createElement(DialogContent, { className: 'max-w-3xl' },
      React.createElement(DialogHeader, null,
        React.createElement(DialogTitle, { className: 'flex items-center gap-2' },
          React.createElement(RefreshCw, { className: 'w-5 h-5 text-orange-600' }),
          'Re-Raise PO — ',
          React.createElement('span', { className: 'text-orange-700' }, sourcePo?.po_number)
        ),
        React.createElement(DialogDescription, { className: 'text-sm text-slate-600 pt-1' },
          'These are the items that have not yet been received. Proceeding will create a new draft PO with only these items, and mark ',
          React.createElement('span', { className: 'font-semibold' }, sourcePo?.po_number),
          ' as Short-Closed.'
        )
      ),
      React.createElement('div', { className: 'mt-3' },
        loading && React.createElement('div', { className: 'flex items-center justify-center py-12 text-slate-500' },
          React.createElement(Loader2, { className: 'w-5 h-5 animate-spin mr-2' }),
          'Loading pending items...'
        ),
        error && React.createElement('div', { className: 'p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-2' },
          React.createElement(AlertCircle, { size: 16 }),
          error
        ),
        !loading && !error && preview && (
          hasPending
            ? React.createElement('div', { className: 'space-y-3', 'data-testid': 'reraise-preview-block' },
                React.createElement('div', { className: 'border rounded overflow-hidden' },
                  React.createElement('table', { className: 'w-full text-sm' },
                    React.createElement('thead', { className: 'bg-slate-50 border-b' },
                      React.createElement('tr', null,
                        React.createElement('th', { className: 'text-left px-3 py-2 text-xs font-semibold text-slate-700' }, '#'),
                        React.createElement('th', { className: 'text-left px-3 py-2 text-xs font-semibold text-slate-700' }, 'Product'),
                        React.createElement('th', { className: 'text-right px-3 py-2 text-xs font-semibold text-slate-700' }, 'Ordered'),
                        React.createElement('th', { className: 'text-right px-3 py-2 text-xs font-semibold text-slate-700' }, 'Received'),
                        React.createElement('th', { className: 'text-right px-3 py-2 text-xs font-semibold text-orange-700 bg-orange-50' }, 'Pending'),
                        React.createElement('th', { className: 'text-right px-3 py-2 text-xs font-semibold text-slate-700' }, 'Rate'),
                        React.createElement('th', { className: 'text-right px-3 py-2 text-xs font-semibold text-slate-700' }, 'GST'),
                        React.createElement('th', { className: 'text-right px-3 py-2 text-xs font-semibold text-slate-700' }, 'Amount')
                      )
                    ),
                    React.createElement('tbody', { className: 'divide-y' },
                      items.map((it, idx) => React.createElement('tr', { key: it.product_id || idx, className: 'hover:bg-slate-50' },
                        React.createElement('td', { className: 'px-3 py-2 text-slate-500' }, idx + 1),
                        React.createElement('td', { className: 'px-3 py-2' },
                          React.createElement('p', { className: 'font-medium text-slate-800' }, it.product_name),
                          it.product_sku && React.createElement('p', { className: 'text-xs text-slate-400' }, it.product_sku)
                        ),
                        React.createElement('td', { className: 'px-3 py-2 text-right text-slate-600' }, it.ordered_quantity),
                        React.createElement('td', { className: 'px-3 py-2 text-right text-slate-600' }, it.received_quantity),
                        React.createElement('td', { className: 'px-3 py-2 text-right font-semibold text-orange-700 bg-orange-50' }, it.pending_quantity),
                        React.createElement('td', { className: 'px-3 py-2 text-right text-slate-600' }, '\u20B9' + (it.rate || 0).toFixed(2)),
                        React.createElement('td', { className: 'px-3 py-2 text-right text-slate-500' }, it.gst_percentage + '%'),
                        React.createElement('td', { className: 'px-3 py-2 text-right font-medium text-slate-800' }, '\u20B9' + (it.line_total || 0).toFixed(2))
                      ))
                    )
                  )
                ),
                React.createElement('div', { className: 'flex items-center justify-between bg-slate-50 border rounded px-4 py-2 text-sm' },
                  React.createElement('div', { className: 'text-slate-600' },
                    React.createElement('span', { className: 'font-semibold' }, summary.items_count),
                    ' pending item' + (summary.items_count === 1 ? '' : 's')
                  ),
                  React.createElement('div', { className: 'flex items-center gap-6' },
                    React.createElement('div', null,
                      React.createElement('span', { className: 'text-slate-500 text-xs' }, 'Subtotal: '),
                      React.createElement('span', { className: 'font-medium' }, '\u20B9' + (summary.pending_subtotal || 0).toFixed(2))
                    ),
                    React.createElement('div', null,
                      React.createElement('span', { className: 'text-slate-500 text-xs' }, 'GST: '),
                      React.createElement('span', { className: 'font-medium' }, '\u20B9' + (summary.pending_gst || 0).toFixed(2))
                    ),
                    React.createElement('div', null,
                      React.createElement('span', { className: 'text-slate-500 text-xs' }, 'Total: '),
                      React.createElement('span', { className: 'font-bold text-emerald-700' }, '\u20B9' + (summary.pending_total || 0).toFixed(2))
                    )
                  )
                )
              )
            : React.createElement('div', { className: 'p-6 bg-slate-50 border border-slate-200 rounded text-center text-slate-600 text-sm' },
                'All items in this PO have been fully received. There is nothing to re-raise.'
              )
        )
      ),
      React.createElement(DialogFooter, { className: 'mt-4' },
        React.createElement(Button, {
          variant: 'outline',
          onClick: () => onOpenChange(false),
          disabled: submitting,
          'data-testid': 'reraise-cancel-btn',
        }, 'Cancel'),
        React.createElement(Button, {
          onClick: handleProceed,
          disabled: !hasPending || submitting || loading,
          className: 'bg-orange-600 hover:bg-orange-700 text-white',
          'data-testid': 'reraise-proceed-btn',
        },
          submitting && React.createElement(Loader2, { className: 'w-4 h-4 mr-1 animate-spin' }),
          submitting ? 'Re-raising...' : 'Proceed & Short-Close'
        )
      )
    )
  );
}

export default ReRaiseDialog;
